import React, { useCallback, useEffect, useState } from "react";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Container,
    Form,
    Modal,
    Pagination,
    Row,
    Table,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    getAllLeaveSetups,
    createLeaveSetup,
    updateLeaveSetup,
    getAllLeaveTypes,
    getAllLeaveCycles,
} from "../../../../api/onboarddeboard";

const LeaveSetup = () => {
    const token = localStorage.getItem("token");
    const flagConfig = [
        { key: "allowContinuous", label: "Allow Continuous Leave" },
        { key: "allowAfterJoining", label: "Allow to Fill After Joining" },
        { key: "allowAtJoiningMonth", label: "Allow to Fill at Joining Month" },
        { key: "sandwichLeave", label: "Sandwich Leave" },
        { key: "carryLeave", label: "Carry Leave (Forward)" },
        { key: "mandatoryHandover", label: "Mandatory Charge Handover" },
    ];

    const [editingId, setEditingId] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const [leaveTypes, setLeaveTypes] = useState([]);
    const [leaveTypeId, setLeaveTypeId] = useState("");
    const [leaveCycles, setLeaveCycles] = useState([]);
    const [leaveCycleId, setLeaveCycleId] = useState("");
    const [gender, setGender] = useState("");

    const [flags, setFlags] = useState({
        allowContinuous: true,
        allowAfterJoining: true,
        allowAtJoiningMonth: true,
        sandwichLeave: false,
        carryLeave: true,
        mandatoryHandover: false,
    });

    const [minDays, setMinDays] = useState("");
    const [maxDays, setMaxDays] = useState("");
    const [cappingDays, setCappingDays] = useState("");
    const [status, setStatus] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOption, setSortOption] = useState("latest");
    const [filterStatus, setFilterStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 20;
    const [errors, setErrors] = useState({});

    const [showReasonModal, setShowReasonModal] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [deactivateReason, setDeactivateReason] = useState("");
    const [reasonErrors, setReasonErrors] = useState({});

    const fetchData = useCallback(async () => {
        try {
            const res = await getAllLeaveSetups(token);
            const data = res?.data?.data || res?.data || [];

            const formatted = data.map((item) => ({
                id: item.id,
                leaveName: item.leaveType?.leaveName,
                leaveCode: item.leaveType?.leaveCode,
                leaveCycle: item.leaveCycle?.cycleName,
                status: item.status,
                leaveTypeId: item.leaveType?.id,
                leaveCycleId: item.leaveCycle?.id,
                gender: item.gender,
                allowContinuous: item.allowContinuousLeave,
                allowAfterJoining: item.allowToFillAfterJoining,
                allowAtJoiningMonth: item.allowToFillAtJoiningMonth,
                sandwichLeave: item.sandwichLeave,
                carryLeave: item.carryLeave,
                mandatoryHandover: item.mandatoryChargeHandover,
                minDays: item.minDaysToApply,
                maxDays: item.maxDaysToApply,
                cappingDays: item.cappingDays,
            }));

            setTableData(formatted);
        } catch (error) {
            toast.error("Failed to fetch Leave Setup");
        }
    }, [token]);

    const fetchLeaveTypes = useCallback(async () => {
        try {
            const res = await getAllLeaveTypes(token);
            setLeaveTypes(res.data || []);
        } catch {
            toast.error("Failed to fetch Leave Types");
        }
    }, [token]);

    const fetchLeaveCycles = useCallback(async () => {
        try {
            const res = await getAllLeaveCycles(token);
            setLeaveCycles(res?.data?.data || res?.data || []);
        } catch {
            toast.error("Failed to fetch Leave Cycles");
        }
    }, [token]);

    useEffect(() => {
        if (token) {
            fetchData();
            fetchLeaveTypes();
            fetchLeaveCycles();
        }
        resetFormState();
    }, [token, fetchData, fetchLeaveTypes, fetchLeaveCycles]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortOption, filterStatus]);

    const resetFormState = () => {
        setEditingId(null);
        setOriginalData(null);
        setLeaveTypeId("");
        setLeaveCycleId("");
        setGender("");
        setFlags({
            allowContinuous: true,
            allowAfterJoining: true,
            allowAtJoiningMonth: true,
            sandwichLeave: false,
            carryLeave: true,
            mandatoryHandover: false,
        });
        setMinDays("");
        setMaxDays("");
        setCappingDays("");
        setStatus(true);
        setErrors({});
        setIsFormSubmitted(false);
    };

    const filteredData = tableData
        .filter((item) => {
            const matchesSearch =
                item.leaveName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.leaveCode?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = filterStatus === "" || item.status.toString() === filterStatus;
            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            if (sortOption === "nameAZ") return a.leaveName?.localeCompare(b.leaveName || "") || 0;
            return b.id - a.id;
        });

    const paginatedData = filteredData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
     
   const getDaysFieldError = (value, { required = false, label }) => {
        const v = String(value ?? "");

        if (v.length === 0) {
            return required ? `${label} is required` : "";
        }
        if (/^\s+$/.test(v)) {
            return `${label} cannot be only spaces.`;
        }
        if (/[a-zA-Z]/.test(v)) {
            return "Alphabets are not allowed. Only numbers allowed.";
        }
        if (/[^0-9]/.test(v)) {
            return "Special characters are not allowed. Only numbers allowed.";
        }
        if (v.length > 3) {
            return `${label} cannot exceed 3 digits`;
        }
        if (required && Number(v) <= 0) {
            return `${label} must be greater than 0`;
        }
        return "";
    };
    const validateForm = () => {
        let newErrors = {};
        if (!leaveTypeId) newErrors.leaveTypeId = "Leave Type is required";
        if (!leaveCycleId) newErrors.leaveCycleId = "Leave Cycle is required";
        if (!maxDays) {
            newErrors.maxDays = "Maximum Days to Apply is required";
        } else if (Number(maxDays) <= 0) {
            newErrors.maxDays = "Maximum Days must be greater than 0";
        }
                const maxDaysCharErr = getDaysFieldError(maxDays, { required: true, label: "Maximum Days" });
        if (maxDaysCharErr) newErrors.maxDays = maxDaysCharErr;

        if (minDays && Number(minDays) > Number(maxDays)) {
            newErrors.minDays = "Minimum Days cannot be greater than Maximum Days";
        }
        if (minDays && Number(minDays) < 0) {
            newErrors.minDays = "Minimum Days cannot be negative";
        }
        if (cappingDays !== "" && Number(cappingDays) < 0) {
            newErrors.cappingDays = "Capping Days cannot be negative";
        }

                const cappingCharErr = getDaysFieldError(cappingDays, { required: false, label: "Capping Days" });
        if (cappingCharErr) newErrors.cappingDays = cappingCharErr;

        const minDaysCharErr = getDaysFieldError(minDays, { required: false, label: "Minimum Days" });
        if (minDaysCharErr) newErrors.minDays = minDaysCharErr;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const hasChanges = () => {
        if (!originalData) return true;
        return (
            Number(leaveTypeId) !== originalData.leaveTypeId ||
            leaveCycleId !== originalData.leaveCycleId ||
            gender !== originalData.gender ||
            flags.allowContinuous !== originalData.allowContinuous ||
            flags.allowAfterJoining !== originalData.allowAfterJoining ||
            flags.allowAtJoiningMonth !== originalData.allowAtJoiningMonth ||
            flags.sandwichLeave !== originalData.sandwichLeave ||
            flags.carryLeave !== originalData.carryLeave ||
            flags.mandatoryHandover !== originalData.mandatoryHandover ||
            Number(minDays) !== originalData.minDays ||
            Number(maxDays) !== originalData.maxDays ||
            Number(cappingDays || 0) !== (originalData.cappingDays || 0) ||
            status !== originalData.status
        );
    };

    const submitData = async (payload) => {
        try {
            if (editingId) {
                await updateLeaveSetup(editingId, payload, token);
            } else {
                await createLeaveSetup(payload, token);
            }
            toast.success(editingId ? "Updated successfully" : "Saved successfully");
            await fetchData();
            resetFormState();
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Failed to submit");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsFormSubmitted(true);
        if (!validateForm()) return;

        if (!editingId) {
            const duplicate = tableData.some(
                (item) => item.leaveTypeId === Number(leaveTypeId) && item.leaveCycleId === Number(leaveCycleId) && item.id !== editingId
            );
            if (duplicate) {
                toast.error("Leave Setup already exists for this Leave Type");
                return;
            }
        }

        if (editingId && !hasChanges()) {
            toast.warning("No changes detected");
            return;
        }

        const payload = {
            leaveTypeId: Number(leaveTypeId),
            leaveCycleId: Number(leaveCycleId),
            gender: gender || null,
            allowContinuousLeave: flags.allowContinuous,
            allowToFillAfterJoining: flags.allowAfterJoining,
            allowToFillAtJoiningMonth: flags.allowAtJoiningMonth,
            sandwichLeave: flags.sandwichLeave,
            carryLeave: flags.carryLeave,
            mandatoryChargeHandover: flags.mandatoryHandover,
            minDaysToApply: minDays === "" ? 0 : Number(minDays),
            maxDaysToApply: Number(maxDays),
            cappingDays: cappingDays === "" ? null : Number(cappingDays),
            status,
        };

        if (editingId && originalData?.status === true && status === false) {
            setPendingPayload(payload);
            setShowReasonModal(true);
            return;
        }

        submitData(payload);
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setErrors({});
        setOriginalData(null);
        setIsFormSubmitted(false);

        setLeaveTypeId(item.leaveTypeId);
        setLeaveCycleId(item.leaveCycleId?.toString() || "");
        setGender(item.gender || "");

        setFlags({
            allowContinuous: item.allowContinuous,
            allowAfterJoining: item.allowAfterJoining,
            allowAtJoiningMonth: item.allowAtJoiningMonth,
            sandwichLeave: item.sandwichLeave,
            carryLeave: item.carryLeave,
            mandatoryHandover: item.mandatoryHandover,
        });

        setMinDays(item.minDays?.toString() || "");
        setMaxDays(item.maxDays?.toString() || "");
        setCappingDays(item.cappingDays?.toString() || "");
        setStatus(item.status);

        setOriginalData({
            leaveTypeId: item.leaveTypeId,
            leaveCycleId: item.leaveCycleId?.toString() || "",
            gender: item.gender || "",
            allowContinuous: item.allowContinuous,
            allowAfterJoining: item.allowAfterJoining,
            allowAtJoiningMonth: item.allowAtJoiningMonth,
            sandwichLeave: item.sandwichLeave,
            carryLeave: item.carryLeave,
            mandatoryHandover: item.mandatoryHandover,
            minDays: item.minDays || 0,
            maxDays: item.maxDays,
            cappingDays: item.cappingDays || null,
            status: item.status,
        });
    };

    const handleCancel = () => resetFormState();

    const handleDeactivateReasonChange = (e) => {
        const value = e.target.value;
        if (value.length > 300) {
            setReasonErrors({ deactivateReason: "Reason cannot exceed 300 characters." });
            return;
        }
        if (value.length === 1 && !/[a-zA-Z0-9]/.test(value)) {
            setReasonErrors({ deactivateReason: "First character cannot be a special character." });
            return;
        }
        if (value.trim() && !/[a-zA-Z]/.test(value)) {
            setReasonErrors({ deactivateReason: "Reason must contain at least one letter." });
            return;
        }
        setDeactivateReason(value);
        setReasonErrors({});
    };

    const handleConfirmReason = () => {
        if (Object.keys(reasonErrors).length > 0) return;
        const updatedPayload = { ...pendingPayload, reason: deactivateReason.trim() || null };
        submitData(updatedPayload);
        setShowReasonModal(false);
        setPendingPayload(null);
        setDeactivateReason("");
        setReasonErrors({});
    };

    const exportToCSV = () => {
        if (filteredData.length === 0) {
            toast.error("No data available to export");
            return;
        }
        const headers = ["Leave Name", "Leave Code", "Leave Cycle", "Status"];
        const rows = filteredData.map((item) => [
            item.leaveName,
            item.leaveCode || "-",
            item.leaveCycle ? item.leaveCycle.toString().toUpperCase() : "-",
            item.status ? "Active" : "Inactive",
        ]);
        const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "leave_setup.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const showError = (fieldName) => errors[fieldName];

    return (
        <Container fluid className="p-4">
            <style>{`
                .leave-toggle .form-check-input:checked { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
                .leave-toggle .form-check-input:focus { border-color: #2E5CB8 !important; box-shadow: 0 0 0 0.1rem rgba(46, 92, 184, 0.35) !important; }
                .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
                .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
                .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
            `}</style>
            <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Leave Setup</h4>
            <ToastContainer position="top-right" autoClose={3000} />
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
                <Breadcrumb.Item href="#">Leave Details</Breadcrumb.Item>
                <Breadcrumb.Item active>Leave Setup</Breadcrumb.Item>
            </Breadcrumb>

            <Row className="mb-4">
                <Col xl={12} lg={12} md={12} xs={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleSubmit} noValidate>
                                <Row>
                                    <Col md={6}>
                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Leave Type<span className="text-danger"> *</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Select
                                                    value={leaveTypeId}
                                                    disabled={editingId !== null}
                                                    onChange={(e) => {
                                                        setLeaveTypeId(e.target.value);
                                                        if (isFormSubmitted) setErrors((prev) => ({ ...prev, leaveTypeId: "" }));
                                                    }}
                                                >
                                                    <option value="">Select Leave Type</option>
                                                    {leaveTypes.filter((lt) => lt.status === true).map((lt) => (
                                                        <option key={lt.id} value={lt.id}>{lt.leaveName}</option>
                                                    ))}
                                                </Form.Select>
                                                {showError("leaveTypeId") && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.leaveTypeId}</div>}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">
                                                Leave Cycle<span className="text-danger"> *</span>
                                            </Form.Label>
                                            <Col sm={8}>
                                                <Form.Select
                                                    value={leaveCycleId}
                                                    onChange={(e) => {
                                                        setLeaveCycleId(e.target.value);
                                                        if (isFormSubmitted) setErrors((prev) => ({ ...prev, leaveCycleId: "" }));
                                                    }}
                                                >
                                                    <option value="">Select Leave Cycle</option>
                                                  {leaveCycles.filter((cycle) => cycle.isActive === true).map((cycle) => (
    <option key={cycle.id} value={cycle.id}>{cycle.cycleName}</option>
))}

                                                </Form.Select>
                                                {showError("leaveCycleId") && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.leaveCycleId}</div>}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Label className="col-sm-4 col-form-label form-label">Gender</Form.Label>
                                            <Col sm={8}>
                                                <Form.Select value={gender} onChange={(e) => setGender(e.target.value)}>
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </Form.Select>
                                            </Col>
                                        </Row>

                                        <div className="mt-4">
                                            <Row className="mb-3">
                                                <Form.Label className="col-sm-4 col-form-label form-label">Minimum Days to Apply</Form.Label>
                                                <Col sm={8}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Optional"
                                                        maxLength={3}
                                                        value={minDays ?? ""}
                                                        onChange={(e) => {
                                                            const value = e.target.value.slice(0, 3);
                                                            setMinDays(value);
                                                            setErrors((prev) => ({ ...prev, minDays: getDaysFieldError(value, { required: false, label: "Minimum Days" }) }));
                                                        }}
                                                    />
                                                    {showError("minDays") && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.minDays}</div>}
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Label className="col-sm-4 col-form-label form-label">
                                                    Maximum Days to Apply <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Col sm={8}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter max days"
                                                        maxLength={3}
                                                        value={maxDays ?? ""}
                                                        onChange={(e) => {
                                                            const value = e.target.value.slice(0, 3);
                                                            setMaxDays(value);
                                                            setErrors((prev) => ({ ...prev, maxDays: value ? getDaysFieldError(value, { required: true, label: "Maximum Days" }) : "" }));
                                                        }}
                                                    />
                                                    {showError("maxDays") && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.maxDays}</div>}
                                                </Col>
                                            </Row>

                                            <Row className="mb-3">
                                                <Form.Label className="col-sm-4 col-form-label form-label">Allowed Days for Capping</Form.Label>
                                               <Col sm={8}>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Optional"
                                                        maxLength={3}
                                                        value={cappingDays || ""}
                                                        onChange={(e) => {
                                                            const value = e.target.value.slice(0, 3);
                                                            setCappingDays(value);
                                                            setErrors((prev) => ({ ...prev, cappingDays: getDaysFieldError(value, { required: false, label: "Capping Days" }) }));
                                                        }}
                                                    />
                                                    {showError("cappingDays") && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.cappingDays}</div>}
                                                </Col>
                                            </Row>
                                        </div>
                                    </Col>

                                    <Col md={6}>
                                        <div className="ps-md-4">
                                            {flagConfig.map((flag) => (
                                                <div key={flag.key} className="mb-4 pb-2">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <span className="form-label" style={{ marginBottom: 0, fontWeight: "normal" }}>{flag.label}</span>
                                                        <div className="d-flex align-items-center gap-2 leave-toggle">
                                                            <Form.Check
                                                                type="switch"
                                                                checked={flags[flag.key]}
                                                                onChange={(e) => setFlags({ ...flags, [flag.key]: e.target.checked })}
                                                            />
                                                            <span style={{ fontSize: "13px", color: "#6c757d", minWidth: "60px" }}>
                                                                {flags[flag.key] ? "Active" : "Inactive"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="mt-3 pt-2">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <span className="form-label" style={{ marginBottom: 0, fontWeight: "normal" }}>Status</span>
                                                    <div className="d-flex align-items-center gap-2 leave-toggle">
                                                        <Form.Check type="switch" checked={status} onChange={(e) => setStatus(e.target.checked)} />
                                                        <span style={{ fontSize: "13px", color: "#6c757d", minWidth: "60px" }}>{status ? "Active" : "Inactive"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="align-items-center mt-4">
                                    <Col className="mt-2 d-flex align-items-center justify-content-center">
                                        <Button variant="primary" type="submit" style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>
                                            {editingId ? "Update" : "Submit"}
                                        </Button>
                                        <Button variant="secondary" type="button" style={{ marginLeft: "10px" }} onClick={handleCancel}>Cancel</Button>
                                    </Col>
                                </Row>
                            </Form>

                            <Row className="mb-4 mt-5">
                                <Col xl={12} lg={12} md={12} xs={12}>
                                    <Row className="mb-3 g-2 align-items-center justify-content-end flex-wrap">
                                        <Col xs={12} sm={6} md={3}>
                                            <div style={{ position: "relative" }}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search by Leave Name or Code..."
                                                    value={searchTerm}
                                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                                    style={{ paddingRight: "35px" }}
                                                />
                                                {searchTerm && (
                                                    <i
                                                        className="bi bi-x-circle"
                                                        onClick={() => { setSearchTerm(""); setCurrentPage(1); }}
                                                        style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#999", fontSize: "16px" }}
                                                    />
                                                )}
                                            </div>
                                        </Col>
                                        <Col xs={6} sm={3} md={2}>
                                            <Form.Select value={sortOption} onChange={(e) => { setSortOption(e.target.value); setCurrentPage(1); }}>
                                                <option value="latest">Latest First</option>
                                                <option value="nameAZ">Name A→Z</option>
                                            </Form.Select>
                                        </Col>
                                        <Col xs={6} sm={3} md={2}>
                                            <Form.Select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                                                <option value="">All Status</option>
                                                <option value="true">Active</option>
                                                <option value="false">Inactive</option>
                                            </Form.Select>
                                        </Col>
                                        <Col xs="auto" className="d-flex justify-content-end" md="auto">
                                            <i
                                                className="bi bi-download rounded"
                                                title="Export CSV"
                                                style={{ cursor: "pointer", color: "#2E5CB8", border: "1px solid #2E5CB8", padding: "7px", paddingLeft: "10px", paddingRight: "10px" }}
                                                onClick={exportToCSV}
                                            />
                                        </Col>
                                    </Row>

                                    <Table bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "8%", textAlign: "center" }}>Sr. No.</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "25%" }}>Leave Name</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "20%", textAlign: "center" }}>Leave Code</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "20%", textAlign: "center" }}>Leave Cycle</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "15%", textAlign: "center" }}>Status</th>
                                                <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "12%", textAlign: "center" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedData.length > 0 ? (
                                                paginatedData.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td style={{ textAlign: "center" }}>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                        <td>{item.leaveName}</td>
                                                        <td style={{ textAlign: "center" }}>{item.leaveCode || "-"}</td>
                                                        <td style={{ textAlign: "center" }}>{item.leaveCycle || "-"}</td>
                                                        <td style={{ textAlign: "center" }}>{item.status ? "Active" : "Inactive"}</td>
                                                        <td className="text-center align-middle">
                                                            <Button variant="secondary" size="sm" onClick={() => handleEdit(item)}>
                                                                <i className="bi bi-pencil-square"></i> Edit
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted">
                                                        {searchTerm || filterStatus ? "No matching records found" : "No data available"}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>

                                    <div className="d-flex justify-content-end">
                                        <Pagination>
                                            <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1}>‹</Pagination.Prev>
                                            <Pagination.Item active>{currentPage}</Pagination.Item>
                                            <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages || 1))} disabled={currentPage === totalPages || totalPages === 0}>›</Pagination.Next>
                                        </Pagination>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showReasonModal} onHide={() => { setShowReasonModal(false); setPendingPayload(null); setDeactivateReason(""); setReasonErrors({}); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title><i className="bi bi-exclamation-triangle-fill text-danger me-2" />Deactivate Leave Setup</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">You are about to deactivate this Leave Setup. Please provide a reason (optional).</p>
                    <Form.Group>
                        <Form.Label>Reason <span className="text-muted">(optional)</span></Form.Label>
                        <Form.Control as="textarea" rows={3} value={deactivateReason} onChange={handleDeactivateReasonChange} placeholder="Enter reason for deactivation..." />
                        <div style={{ fontSize: "12px", color: deactivateReason.length > 300 ? "red" : "#6c757d", textAlign: "right", marginTop: "4px" }}>
                            {deactivateReason.length}/300 characters
                        </div>
                        {reasonErrors.deactivateReason && <p style={{ color: "red", fontSize: "13px", marginTop: "8px" }}>{reasonErrors.deactivateReason}</p>}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowReasonModal(false); setPendingPayload(null); setDeactivateReason(""); setReasonErrors({}); }}>Cancel</Button>
                    <Button variant="danger" onClick={handleConfirmReason}>Confirm Deactivation</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LeaveSetup;
